/* iceberg-ui */
import {
    Box,
    Button,
    Flex,
    Flyout,
    ModalFooter
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { Typography } from '@mui/material';
import { Z_INDEX } from 'application/Shared/Constants';
import { ReportParams } from 'application/Shared/types/reports';

/* React */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Components */
import { ReportBuilder } from './ReportBuilder';
import { ReportHasNoData } from './internal/ReportHasNoData';

interface EditReportFlyoutProps {
    noData: boolean;
    onClose: () => void;
    onRunReport: (reportParams: ReportParams) => void;
    reportParams: ReportParams;
    reportType: string;
}

/**
 * Flyout component that allows users to edit report parameters live once rendered.
 *
 * This component provides the same options as the report setup page but in a flyout
 * format, allowing users to modify report parameters without losing context of the
 * current report view. Once selections have been made, the user can re-run the report
 * to visualize the results.
 *
 * @param noData - Boolean indicating absent report data, employed to show the no data alert at the top of the flyout
 * @param onClose - Function to call when the flyout should be closed
 * @param onRunReport - Function to call when the user wants to run the report with new parameters
 * @param reportParams - Current report parameters to edit
 * @param reportType - Type of report being edited
 */
export const EditReportFlyout = ({
    noData,
    onClose,
    onRunReport,
    reportParams
}: EditReportFlyoutProps) => {
    const { t } = useTranslation();
    const [editedReportParams, setEditedReportParams] = useState(reportParams);

    const handleRunReport = () => {
        onRunReport(editedReportParams);
        onClose();
    };

    return (
        <Flyout
            data-testid="editReportFlyout"
            onClose={onClose}
            title={t('Reports.editReport')}
            headerIcon="Pencil"
            width="480px"
            zIndex={Z_INDEX.FLYOUT_LOW}
        >
            <Flex display="flex" flexDirection="column" height="100%">
                {noData && (
                    <Box p="32px" paddingBottom="0">
                        <ReportHasNoData />
                    </Box>
                )}
                <Flex
                    display="flex"
                    flexDirection="column"
                    padding="16px"
                    paddingBottom="0px"
                    flex="1"
                    overflow="auto"
                >
                    <Typography variant="body1" padding="16px">
                        {t('Reports.editReportDescription')}
                    </Typography>
                    <ReportBuilder
                        reportParams={editedReportParams}
                        setReportParams={setEditedReportParams}
                    />
                </Flex>
                <ModalFooter
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}
                >
                    <Button
                        data-trackid="edit_report_flyout_cancel_btn"
                        variant="secondary"
                        onClick={onClose}
                    >
                        {t('Common.cancel')}
                    </Button>
                    <Button
                        data-trackid="edit_report_flyout_runReport_btn"
                        disabled={!editedReportParams.endTime}
                        variant="primary"
                        onClick={handleRunReport}
                    >
                        {t('Reports.run_report')}
                    </Button>
                </ModalFooter>
            </Flex>
        </Flyout>
    );
};
